import React, { useState, useEffect } from 'react';
import Layout from './Layout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

function MarketplaceDashboard() {
  const [activeTab, setActiveTab] = useState('apps'); // 'apps' or 'installed'
  const [apps, setApps] = useState([]);
  const [installations, setInstallations] = useState([]);
  const [instances, setInstances] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedApp, setSelectedApp] = useState(null);
  const [showInstallModal, setShowInstallModal] = useState(false);

  const token = localStorage.getItem('access_token');

  const categories = [
    { value: 'all', label: 'All Apps' },
    { value: 'cms', label: 'CMS & Publishing' },
    { value: 'collaboration', label: 'Collaboration' },
    { value: 'development', label: 'Development' },
    { value: 'database', label: 'Databases' },
    { value: 'analytics', label: 'Analytics' },
    { value: 'automation', label: 'Automation' },
  ];

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [activeTab, selectedCategory]);

  const fetchData = async () => {
    try {
      const instancesRes = await fetch(`${API_URL}/v1/instances`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const instancesData = await instancesRes.json();
      setInstances(instancesData.instances || []);

      if (activeTab === 'apps') {
        const categoryParam = selectedCategory !== 'all' ? `?category=${selectedCategory}` : '';
        const appsRes = await fetch(`${API_URL}/v1/marketplace/apps${categoryParam}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const appsData = await appsRes.json();
        setApps(appsData.apps || []);
      } else {
        const installsRes = await fetch(`${API_URL}/v1/marketplace/installations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const installsData = await installsRes.json();
        setInstallations(installsData.installations || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const installApp = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      const response = await fetch(`${API_URL}/v1/marketplace/install`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          instance_id: formData.get('instance_id'),
          app_slug: selectedApp.slug
        })
      });

      const data = await response.json();
      if (response.ok) {
        alert(`${selectedApp.name} installation started!\n\nAccess URL: ${data.access_url || 'Will be available soon'}\n\n${selectedApp.post_install_message || ''}`);
        setShowInstallModal(false);
        fetchData();
      } else {
        alert('Error: ' + (data.error || 'Installation failed'));
      }
    } catch (error) {
      alert('Error installing app: ' + error.message);
    }
  };

  const uninstallApp = async (installationId, appName) => {
    if (!window.confirm(`Uninstall ${appName}? This cannot be undone.`)) return;
    try {
      await fetch(`${API_URL}/v1/marketplace/installations/${installationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      alert('Error uninstalling app: ' + error.message);
    }
  };

  const getCategoryBadgeColor = (category) => {
    const colors = {
      cms: 'bg-purple-900 text-purple-300',
      collaboration: 'bg-blue-900 text-blue-300',
      development: 'bg-green-900 text-green-300',
      database: 'bg-red-900 text-red-300',
      analytics: 'bg-yellow-900 text-yellow-300',
      automation: 'bg-pink-900 text-pink-300',
    };
    return colors[category] || 'bg-gray-700 text-gray-300';
  };

  return (
    <Layout userType="customer">
      <div className="max-w-7xl mx-auto p-6">
        <h1 className="text-3xl font-bold text-white mb-6">App Marketplace</h1>

        {/* Tabs */}
        <div className="flex space-x-4 mb-6">
          <button
            onClick={() => setActiveTab('apps')}
            className={`px-6 py-3 rounded-lg font-medium ${
              activeTab === 'apps'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            Browse Apps
          </button>
          <button
            onClick={() => setActiveTab('installed')}
            className={`px-6 py-3 rounded-lg font-medium ${
              activeTab === 'installed'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            Installed Apps
          </button>
        </div>

        {/* Category Filter (for Browse Apps) */}
        {activeTab === 'apps' && (
          <div className="mb-6 flex space-x-2">
            {categories.map(cat => (
              <button
                key={cat.value}
                onClick={() => setSelectedCategory(cat.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  selectedCategory === cat.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:text-white'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        )}

        {/* App Cards Grid */}
        {activeTab === 'apps' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {apps.map((app) => (
              <div key={app.id} className="bg-gray-800 rounded-lg p-6 border border-gray-700 hover:border-blue-500 transition">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    {app.icon_url ? (
                      <img src={app.icon_url} alt={app.name} className="w-12 h-12 rounded mr-3" />
                    ) : (
                      <div className="w-12 h-12 rounded bg-blue-600 flex items-center justify-center mr-3 text-white font-bold text-xl">
                        {app.name.charAt(0)}
                      </div>
                    )}
                    <div>
                      <h3 className="text-lg font-bold text-white">{app.name}</h3>
                      {app.featured && (
                        <span className="text-xs text-yellow-400">⭐ Featured</span>
                      )}
                    </div>
                  </div>
                </div>

                <p className="text-gray-400 text-sm mb-4 line-clamp-2">{app.description}</p>

                <div className="flex items-center justify-between mb-4">
                  <span className={`px-2 py-1 rounded text-xs ${getCategoryBadgeColor(app.category)}`}>
                    {app.category}
                  </span>
                  <span className="text-gray-500 text-xs">{app.install_count} installs</span>
                </div>

                <div className="text-xs text-gray-500 mb-4">
                  Requires: {app.min_vcpus} vCPU, {app.min_ram_gb} GB RAM
                </div>

                <button
                  onClick={() => {
                    setSelectedApp(app);
                    setShowInstallModal(true);
                  }}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium"
                >
                  Install
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Installed Apps Table */}
        {activeTab === 'installed' && (
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">App</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Instance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Access</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Installed</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {installations.map((install) => (
                  <tr key={install.id}>
                    <td className="px-6 py-4 text-white font-medium">{install.app_name}</td>
                    <td className="px-6 py-4 text-gray-400">
                      {instances.find(i => i.id === install.instance_id)?.name || install.instance_id.substring(0, 8)}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${
                        install.status === 'running' ? 'bg-green-900 text-green-300' :
                        install.status === 'installing' ? 'bg-blue-900 text-blue-300' :
                        install.status === 'failed' ? 'bg-red-900 text-red-300' :
                        'bg-gray-700 text-gray-300'
                      }`}>
                        {install.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {install.access_url ? (
                        <a
                          href={install.access_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:text-blue-300"
                        >
                          Open App
                        </a>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-400 text-sm">
                      {new Date(install.installed_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => uninstallApp(install.id, install.app_name)}
                        className="text-red-400 hover:text-red-300"
                      >
                        Uninstall
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {installations.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                No apps installed yet. Browse the marketplace to get started!
              </div>
            )}
          </div>
        )}
      </div>

      {/* Install Modal */}
      {showInstallModal && selectedApp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-white mb-4">Install {selectedApp.name}</h3>
            <p className="text-gray-400 mb-4">{selectedApp.description}</p>

            <div className="bg-gray-700 rounded p-3 mb-4 text-sm">
              <div className="text-gray-300 mb-1"><strong>Requirements:</strong></div>
              <div className="text-gray-400">• {selectedApp.min_vcpus} vCPU minimum</div>
              <div className="text-gray-400">• {selectedApp.min_ram_gb} GB RAM minimum</div>
              <div className="text-gray-400">• {selectedApp.min_storage_gb} GB storage minimum</div>
            </div>
            
            <form onSubmit={installApp}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Select Instance
                </label>
                <select
                  name="instance_id"
                  required
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                >
                  <option value="">Choose an instance...</option>
                  {instances
                    .filter(i => i.status === 'running' && 
                                 i.vcpus >= selectedApp.min_vcpus && 
                                 i.ram_gb >= selectedApp.min_ram_gb)
                    .map(instance => (
                      <option key={instance.id} value={instance.id}>
                        {instance.name} ({instance.vcpus} vCPU, {instance.ram_gb} GB RAM)
                      </option>
                    ))}
                </select>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowInstallModal(false)}
                  className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Install Now
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default MarketplaceDashboard;
